import { CARS } from './cars.ts';
import { carsIn, categoryOf } from '../../shared/categories.ts';
import type { CharacterId } from './characters.ts';
import targets from './targets.json' with { type: 'json' };

/**
 * Locked cars, and what opens each one.
 *
 * A car with a challenge here starts locked, and opens for good once its
 * challenge is done on enough courses. The challenge is driven in the locked
 * car's own category -- to earn the best STREET car, prove it in STREET -- so
 * the slower cars in a category are the way to the fastest.
 *
 * Three kinds, each counted once per course:
 *
 *   drift   score the course's target in a Drift Run;
 *   time    beat the course's target time in Time Attack, Easy or Pro;
 *   tandem  win a Drift Tandem battle against `rival`, or anyone harder.
 *
 * Targets are set per course and per car from what the house driver TAKA
 * does in that car (tools/targets.mjs, `npm run cars:targets`), so every car
 * in the category has a fair one. A new car with a challenge is a new line
 * below, and a run of the tool.
 */

export type ChallengeKind = 'drift' | 'time' | 'tandem';

export interface Challenge {
  /** The car it unlocks. */
  carId: string;
  kind: ChallengeKind;
  /** Courses it has to be done on. */
  courses: number;
  /** Tandem only: the house driver to beat. */
  rival?: CharacterId;
}

export const CHALLENGES: Challenge[] = [
  { carId: 'm4', kind: 'drift', courses: 3 },
  { carId: 'rx7-fd', kind: 'tandem', courses: 3, rival: 'pro' },
  { carId: 'nsx-gt500', kind: 'time', courses: 3 },
];

export interface CourseTargets {
  /** Route version the targets were set on: a reshaped course has none until they are set again. */
  version: number;
  /** Drift Run points, per car. */
  drift: Record<string, number>;
  /** Time Attack seconds, per car. */
  time: Record<string, number>;
}

export interface TargetFile {
  /** Sim the targets were set on: a change to physics or scoring needs them set again. */
  simVersion: number;
  routes: Record<string, CourseTargets>;
}

export const TARGETS = targets as TargetFile;

export function challengeFor(carId: string): Challenge | undefined {
  return CHALLENGES.find((c) => c.carId === carId);
}

/** The cars a challenge can be driven in: the locked car's category, less any car still locked itself. */
export function challengeCars(challenge: Challenge): string[] {
  const category = categoryOf(challenge.carId);
  if (!category) return [];
  const locked = new Set(CHALLENGES.map((c) => c.carId));
  return carsIn(category.id).filter((id) => !locked.has(id) && CARS.some((c) => c.id === id));
}

/** A course's target for a car, or undefined where none is set (a new course, or a car that cannot earn it). */
export function targetFor(kind: 'drift' | 'time', routeId: string, routeVersion: number, carId: string): number | undefined {
  const course = TARGETS.routes[routeId];
  if (!course || course.version !== routeVersion) return undefined;
  return course[kind][carId];
}
